import { useCallback, useEffect, useState } from 'react';

export const GROUP_FILTER_STORAGE_KEY = 'cmx.manager-home.group-filter';

const readStored = (): string[] | null => {
  try {
    const raw = window.localStorage.getItem(GROUP_FILTER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : null;
  } catch {
    return null;
  }
};

const writeStored = (ids: Set<string>) => {
  try {
    window.localStorage.setItem(
      GROUP_FILTER_STORAGE_KEY,
      JSON.stringify(Array.from(ids)),
    );
  } catch {
    /* noop */
  }
};

const computeInitial = (availableGroupIds: string[]): Set<string> => {
  const available = new Set(availableGroupIds);
  const stored = readStored();
  if (stored === null) return new Set(availableGroupIds);
  return new Set(stored.filter((id) => available.has(id)));
};

export const useGroupFilterPersistence = (availableGroupIds: string[]) => {
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(() =>
    computeInitial(availableGroupIds),
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const stored = readStored();
    const available = new Set(availableGroupIds);
    if (stored === null) {
      setSelectedGroupIds(new Set(availableGroupIds));
      return;
    }
    const filtered = new Set(stored.filter((id) => available.has(id)));
    setSelectedGroupIds(filtered);
  }, [availableGroupIds.join('|')]);

  const toggleGroup = useCallback((groupId: string) => {
    setSelectedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      writeStored(next);
      return next;
    });
  }, []);

  return { selectedGroupIds, toggleGroup };
};
